/**
 * Bank Dialog Component
 * 
 * Dialog for creating or editing banks.
 */

import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2 } from "lucide-react";
import { bankService } from "@/services/bankService";
import { toast } from "sonner";
import type { Bank } from "@/services/bankService";

interface BankDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bank?: Bank | null;
  onSuccess?: () => void;
}

export function BankDialog({ open, onOpenChange, bank, onSuccess }: BankDialogProps) {
  const [name, setName] = React.useState("");
  const [code, setCode] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [status, setStatus] = React.useState<"pending" | "active" | "inactive">("pending");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const isEdit = !!bank;

  // Reset form when dialog opens/closes or bank changes
  React.useEffect(() => {
    if (open) {
      if (bank) {
        setName(bank.name);
        setCode(bank.code);
        setDescription(bank.description || "");
        setStatus(bank.status || "pending");
      } else {
        setName("");
        setCode("");
        setDescription("");
        setStatus("pending");
      }
      setError(null);
    }
  }, [open, bank]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!name.trim()) {
        setError("Nama bank harus diisi");
        setLoading(false);
        return;
      }

      if (!code.trim()) {
        setError("Kode bank harus diisi");
        setLoading(false);
        return;
      }

      if (isEdit && bank) {
        await bankService.updateBank(bank._id, {
          name: name.trim(),
          code: code.trim(),
          description: description?.trim() || undefined,
          status,
        });
        toast.success("Bank berhasil diperbarui");
      } else {
        await bankService.createBank({
          name: name.trim(),
          code: code.trim(),
          description: description?.trim() || undefined,
          status,
        });
        toast.success("Bank berhasil ditambahkan");
      }

      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Gagal menyimpan bank";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = name.trim().length > 0 && code.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Bank" : "Tambah Bank"}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Perbarui informasi bank" : "Tambahkan bank baru ke sistem"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nama Bank *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Contoh: Bank Central Asia"
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="code">Kode Bank *</Label>
              <Input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="Contoh: BCA"
                disabled={loading}
                maxLength={10}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Deskripsi</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Deskripsi bank (opsional)"
                disabled={loading}
                rows={3}
              />
            </div>

            <div className="space-y-3">
              <Label>Status</Label>
              <RadioGroup
                value={status}
                onValueChange={(value) => setStatus(value as "pending" | "active" | "inactive")}
                disabled={loading}
                className="grid grid-cols-3 gap-4"
              >
                <div className="flex items-center space-x-2 p-3 border rounded-md hover:bg-accent transition-colors cursor-pointer">
                  <RadioGroupItem value="pending" id="status-pending" />
                  <Label htmlFor="status-pending" className="font-normal cursor-pointer flex-1">
                    Pending
                  </Label>
                </div>
                <div className="flex items-center space-x-2 p-3 border rounded-md hover:bg-accent transition-colors cursor-pointer">
                  <RadioGroupItem value="active" id="status-active" />
                  <Label htmlFor="status-active" className="font-normal cursor-pointer flex-1">
                    Active
                  </Label>
                </div>
                <div className="flex items-center space-x-2 p-3 border rounded-md hover:bg-accent transition-colors cursor-pointer">
                  <RadioGroupItem value="inactive" id="status-inactive" />
                  <Label htmlFor="status-inactive" className="font-normal cursor-pointer flex-1">
                    Inactive
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Batal
            </Button>
            <Button type="submit" disabled={!isFormValid || loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? "Perbarui" : "Tambah"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

